// attempt-forensics.ts
//
// On `contract_error`/`recovering`, archive evidence into
// `<control_root>/attempts/<attemptId>/forensics/` BEFORE anything is
// cleaned up (mission-workspace.ts documents `<control_root>` as the
// controller-owned state directory, separate from the worktree). Two kinds
// of evidence:
//   1. A combined patch (`git diff HEAD` for tracked changes + staged diff)
//      plus the list of untracked file names — enough to reconstruct what
//      the worker touched without needing the (possibly compromised)
//      worktree to still exist.
//   2. Arbitrary structured evidence the caller supplies (e.g. binding
//      hash-mismatch details) — redacted through SecretRedactor before
//      being written, since evidence objects may echo attacker-controlled
//      or worker-emitted strings that themselves contain secrets.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { spawnAsyncCapture } from "../spawn-async-capture-with-timeout.js";
import { captureUntrackedFileContents } from "./attempt-forensics-untracked-file-contents.js";
import { knownValuesForControlRoot } from "./mission-secrets-test-env-loader.js";
import { redactKnownValues, SecretRedactor } from "./secret-redactor.js";

export interface ArchiveAttemptForensicsInput {
  controlRoot: string;
  repoDir: string;
  attemptId: string;
  reason: string;
  bindingEvidence?: Record<string, unknown>;
  secretValues?: string[];
  // P13 Agent A: when the verdict is a result-JSON contract_error
  // (`no_result_json_found`/`invalid_structured_output_schema`), the worker's
  // raw `result` text tail + the zod issue field paths that failed —
  // written to `result-schema-error.json` so `summarize-schema-errors.ts`
  // can aggregate which fields fail most often across a control root.
  // `rawResultTail` goes through the SAME redaction pass as the patch/binding
  // evidence above (secretValues) before touching disk.
  rawResultTail?: string;
  schemaErrorPaths?: string[];
  // Phase 06d R7 (B11): also write `untracked-files.json` with the redacted
  // CONTENTS of untracked files (caps in attempt-forensics-untracked-file-
  // contents.ts). Only the archive that precedes a post-route reset sets it —
  // that reset's `git clean -fd` deletes the only copy. Default off: the
  // contract_error/recovering archive keeps its old shape.
  captureUntrackedContents?: boolean;
  /** 3.38.0-beta.2 F-74: the validated result of a refused attempt ⇒ `rejected-result.json` (redacted). */
  rejectedResult?: unknown;
}

export interface ArchiveAttemptForensicsResult {
  forensicsDir: string;
  manifestPath: string;
}

async function git(args: string[], cwd: string): Promise<string> {
  const res = await spawnAsyncCapture("git", args, {
    cwd,
    timeoutMs: 30_000,
    forwardSigint: false,
  });
  return res.stdout;
}

async function listUntracked(repoDir: string): Promise<string[]> {
  const listing = await git(["ls-files", "--others", "--exclude-standard", "-z"], repoDir);
  return listing.split("\0").filter((p) => p.length > 0);
}

/** Audit F-4: forensics hold refused worker output — owner-only, whatever the umask. */
async function privateDir(dir: string): Promise<void> {
  await fs.mkdir(dir, { recursive: true, mode: 0o700 });
  await fs.chmod(dir, 0o700);
}

async function writePrivate(path: string, text: string): Promise<void> {
  await fs.writeFile(path, text, { encoding: "utf8", mode: 0o600 });
  await fs.chmod(path, 0o600); // re-archive over an older file keeps its mode otherwise
}

/** Cap for `rejected-result.json` (redacted first, so a cut never splits a secret unmasked). */
export const REJECTED_RESULT_MAX_CHARS = 256 * 1024;

function redact(text: string, secretValues: string[]): string {
  const redactor = new SecretRedactor({ secretValues });
  return redactor.feed(text) + redactor.flush();
}

/**
 * Archive tracked+staged diff, untracked file list, and any caller-supplied
 * structured evidence (e.g. binding mismatch) for one attempt. Idempotent
 * per attemptId directory — re-archiving overwrites rather than duplicating.
 */
export async function archiveAttemptForensics(
  input: ArchiveAttemptForensicsInput,
): Promise<ArchiveAttemptForensicsResult> {
  const forensicsDir = join(input.controlRoot, "attempts", input.attemptId, "forensics");
  await privateDir(forensicsDir);
  // Phase 04a: hợp known-values của mission (registry, mọi caller) — binding
  // evidence bị stringify TRƯỚC redact nên cần dạng JSON-escape (secret-redactor).
  const secretValues = [
    ...(input.secretValues ?? []),
    ...(await knownValuesForControlRoot(input.controlRoot)),
  ];

  const trackedDiff = await git(["diff", "HEAD"], input.repoDir);
  const stagedDiff = await git(["diff", "--cached"], input.repoDir);
  const combinedPatch = `${trackedDiff}${stagedDiff}`;
  const patchPath = join(forensicsDir, "worktree-changes.patch");
  await writePrivate(patchPath, redact(combinedPatch, secretValues));

  const untrackedFiles = await listUntracked(input.repoDir);
  if (input.captureUntrackedContents === true) {
    await writeUntrackedContents(forensicsDir, input.repoDir, untrackedFiles, secretValues);
  }

  if (input.bindingEvidence !== undefined) {
    const bindingPath = join(forensicsDir, "binding-evidence.json");
    const raw = JSON.stringify(input.bindingEvidence, null, 2);
    await writePrivate(bindingPath, redact(raw, secretValues));
  }

  if (input.rawResultTail !== undefined || input.schemaErrorPaths !== undefined) {
    const schemaErrorPath = join(forensicsDir, "result-schema-error.json");
    const payload = {
      attempt_id: input.attemptId,
      raw_result_tail:
        input.rawResultTail !== undefined ? redact(input.rawResultTail, secretValues) : undefined,
      schema_error_paths: input.schemaErrorPaths ?? [],
    };
    await writePrivate(schemaErrorPath, JSON.stringify(payload, null, 2));
  }

  if (input.rejectedResult !== undefined) {
    const text = redact(JSON.stringify(input.rejectedResult, null, 2), secretValues);
    const capped =
      text.length <= REJECTED_RESULT_MAX_CHARS
        ? text
        : JSON.stringify({
            truncated: true,
            original_chars: text.length,
            head: text.slice(0, REJECTED_RESULT_MAX_CHARS),
          });
    await writePrivate(join(forensicsDir, "rejected-result.json"), capped);
  }

  const manifestPath = join(forensicsDir, "manifest.json");
  const manifest = {
    attempt_id: input.attemptId,
    reason: redactKnownValues(input.reason, secretValues),
    archived_at: new Date().toISOString(),
    untrackedFiles: untrackedFiles.map((path) => redact(path, secretValues)), // review L2
  };
  await writePrivate(manifestPath, JSON.stringify(manifest, null, 2));

  return { forensicsDir, manifestPath };
}

async function writeUntrackedContents(
  forensicsDir: string,
  repoDir: string,
  untrackedFiles: string[],
  secretValues: string[],
): Promise<void> {
  const files = await captureUntrackedFileContents(repoDir, untrackedFiles, (text) =>
    redact(text, secretValues),
  );
  const untrackedPath = join(forensicsDir, "untracked-files.json");
  await writePrivate(untrackedPath, JSON.stringify({ files }, null, 2));
}

/**
 * 06g R2e: adds `untracked-files.json` to an archive the verdict path already
 * wrote (names only) when a worktree reset is about to delete those files —
 * a killed session is a contract_error, and its files were the only copy.
 * Leaves manifest/patch untouched.
 */
export async function addUntrackedContentsToForensics(input: {
  controlRoot: string;
  repoDir: string;
  attemptId: string;
}): Promise<void> {
  const forensicsDir = join(input.controlRoot, "attempts", input.attemptId, "forensics");
  await privateDir(forensicsDir);
  const secretValues = await knownValuesForControlRoot(input.controlRoot);
  const untrackedFiles = await listUntracked(input.repoDir);
  await writeUntrackedContents(forensicsDir, input.repoDir, untrackedFiles, secretValues);
}
